import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { SampleMeta, usePublicSample, useTranslatedList } from './shared';

/** Review fix F11: the public front door at `/` for signed-out visitors (and `/welcome` for
 * anyone). Explains the product, shows a real sample card, the three-step journey, how sources
 * are handled, the actual free/Premium terms and privacy -- before asking for an email. */
export function Landing() {
  const { t } = useTranslation();
  const steps = useTranslatedList<{ title: string; body: string }>('public.landing.steps.items');

  return (
    <div className="landing">
      <section className="landing-hero" aria-labelledby="landing-title">
        <div className="landing-hero-copy">
          <h1 id="landing-title" className="landing-title">{t('public.landing.title')}</h1>
          <p className="landing-lead">{t('public.landing.lead')}</p>
          <p className="landing-support">{t('public.landing.support')}</p>
          <div className="landing-cta-row">
            <Link to="/sample" className="btn btn-primary">{t('public.landing.ctaPrimary')}</Link>
            <Link to="/signup" className="btn btn-secondary">{t('public.landing.ctaSecondary')}</Link>
          </div>
          <p className="landing-note">
            {t('public.landing.ctaNote')}{' '}
            <Link to="/login" className="text-link">{t('public.landing.signInPrompt')}</Link>
          </p>
        </div>
        <figure className="landing-art">
          <img src="/login-hero.jpg" alt={t('public.landing.artAlt')} width={1600} height={893} />
          <figcaption>{t('public.landing.artCaption')}</figcaption>
        </figure>
      </section>

      <section className="landing-section" aria-labelledby="landing-sample-heading">
        <h2 id="landing-sample-heading">{t('public.landing.sample.heading')}</h2>
        <p className="landing-section-intro">{t('public.landing.sample.intro')}</p>
        <SampleCard />
      </section>

      <section className="landing-section" aria-labelledby="landing-steps-heading">
        <h2 id="landing-steps-heading">{t('public.landing.steps.heading')}</h2>
        <ol className="landing-steps" role="list">
          {steps.map((step, i) => (
            <li key={i}>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </li>
          ))}
        </ol>
        <p className="landing-more">
          <Link to="/how-it-works" className="text-link">{t('public.landing.steps.more')}</Link>
        </p>
      </section>

      <div className="landing-section landing-columns">
        <section className="info-card" aria-labelledby="landing-trust-heading">
          <h2 id="landing-trust-heading">{t('public.landing.trust.heading')}</h2>
          <p>{t('public.landing.trust.body')}</p>
          <Link to="/editorial-standards" className="text-link">{t('public.landing.trust.link')}</Link>
        </section>
        <section className="info-card" aria-labelledby="landing-pricing-heading">
          <h2 id="landing-pricing-heading">{t('public.landing.pricing.heading')}</h2>
          <p>{t('public.landing.pricing.free')}</p>
          <p>{t('public.landing.pricing.premium')}</p>
          <Link to="/pricing" className="text-link">{t('public.landing.pricing.link')}</Link>
        </section>
        <section className="info-card" aria-labelledby="landing-privacy-heading">
          <h2 id="landing-privacy-heading">{t('public.landing.privacy.heading')}</h2>
          <p>{t('public.landing.privacy.body')}</p>
          <p className="info-card-links">
            <Link to="/privacy" className="text-link">{t('public.landing.privacy.privacyLink')}</Link>
            <Link to="/support" className="text-link">{t('public.landing.privacy.supportLink')}</Link>
          </p>
        </section>
      </div>
    </div>
  );
}

function SampleCard() {
  const { t } = useTranslation();
  const { status, sample, retry } = usePublicSample();

  if (status === 'loading') {
    return (
      <div className="sample-card" role="status">
        <p className="page-subtitle">{t('public.sample.loading')}</p>
      </div>
    );
  }
  if (status === 'error' || !sample) {
    return (
      <div className="sample-card">
        <p role="alert">{t('public.sample.error')}</p>
        <button type="button" className="btn btn-secondary" onClick={retry}>{t('public.sample.retry')}</button>
      </div>
    );
  }
  return (
    <article className="sample-card" aria-labelledby="sample-card-title">
      <p className="sample-card-eyebrow">{t('public.landing.sample.eyebrow')}</p>
      <h3 id="sample-card-title" className="sample-card-title">{sample.title}</h3>
      <SampleMeta sample={sample} />
      <p className="sample-card-description">{sample.description}</p>
      <Link to="/sample" className="btn btn-primary">{t('public.landing.sample.cta')}</Link>
    </article>
  );
}
